import AsyncStorage from "@react-native-async-storage/async-storage";
import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, ScrollView, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { LangCodes } from "../../../central-atoms/enums/generic/i18n/LangCodes";
import config from "../../../config/config";
import { LS_KEYS, SCREEN_NAMES } from "../../../constants";
import i18n from "../../../lang/i18n";
import ScreenBottomBar from "../../../miscellaneous/components/ScreenBottomBar";
import ScreenHeader from "../../../miscellaneous/components/ScreenHeader";
import { StyleHelpers } from "../../../miscellaneous/constants/style-helpers";
import { ScreenComponent } from "../../../miscellaneous/types/components";
import AuthAction from "../../../redux/reducer/auth/actions";
import { ErrorHandlerService } from "../../../services/ErrorHandlerService";
import { THEME_COLORS } from "../../../theme/constants/themeColors";
import { APP_STATE } from "../../../types";
import CButton from "../../components/CButton";
import CText from "../../components/CText";
import LanguageSelectionCard from "../../components/LanguageSelectionCard";
import styles from "./styles";
import { useRemoteConfig } from "../../../org-app-config/hooks";

type LanguageMapsType = {
  id: LangCodes,
  transTitle: string,
  langText: string,
  btnText: string,
}

type Props = ScreenComponent & {};
export default function SelectLanguageScreen(props: Props) {
  const { navigation, route } = props;
  const { t } = useTranslation();
  const type = (route?.params as any)?.type;
  const { currentLanguage } = useSelector((state: APP_STATE) => state.auth);
  const { setCurrentLanguage } = AuthAction;
  const dispatch = useDispatch();

  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

  const remoteConfig = useRemoteConfig();

  const LanguageMaps = useMemo(() => ({
    [LangCodes.kn]: {
      id: LangCodes.kn,
      transTitle: t("kannada"),
      langText: "ಕನ್ನಡ",
      btnText: "ಆಯ್ಕೆ ಮಾಡಿ",
    },
    [LangCodes.hi]: {
      id: LangCodes.hi,
      transTitle: t("hindi"),
      langText: "हिन्दी",
      btnText: "चुन लिया",
    },
    [LangCodes.ta]: {
      id: LangCodes.ta,
      transTitle: t("tamil"),
      langText: "தமிழ்",
      btnText: "தேர்ந்தெடு",
    },
    [LangCodes.en]: {
      id: LangCodes.en,
      transTitle: t("english"),
      langText: "English",
      btnText: "Select",
    },
    [LangCodes.mr]: {
      id: LangCodes.mr,
      transTitle: t("marathi"),
      langText: "मराठी",
      btnText: "निवडा",
    },
    [LangCodes.te]: {
      id: LangCodes.te,
      transTitle: t("telugu"),
      langText: "తెలుగు",
      btnText: "ఎంచుకోండి",
    },
    [LangCodes.gu]: {
      id: LangCodes.gu,
      transTitle: t("gujarati"),
      langText: "ગુજરાતી",
      btnText: "પસંદ કરો",
    },
    [LangCodes.bn]: {
      id: LangCodes.bn,
      transTitle: t("bengali"),
      langText: "বাংলা",
      btnText: "নির্বাচন করুন",
    },
    [LangCodes.ml]: {
      id: LangCodes.ml,
      transTitle: t("malayalam"),
      langText: "മലയാളം",
      btnText: "തിരഞ്ഞെടുക്കുക",
    },
    [LangCodes.ur]: {
      id: LangCodes.ur,
      transTitle: t('urdu'),
      langText: 'اردو',
      btnText: 'منتخب کریں',
    },
    [LangCodes.ne]: {
      id: LangCodes.ne,
      transTitle: t('nepali'),
      langText: 'नेपाली',
      btnText: 'चयन गर्नुहोस्',
    },
  }), [t]);

  // const languageOptions = Object.values(LanguageMaps);

  const languageOptions = useMemo(() => {
    if (isEmpty(remoteConfig?.supported_languages)) {
      // show all languages
      return Object.values(LanguageMaps);
    }

    const languages: LanguageMapsType[] = [];
    remoteConfig?.supported_languages?.forEach((lang: LangCodes) => {
      if (LanguageMaps[lang]) {
        languages.push(LanguageMaps[lang]);
      }
    });

    return languages;
  }, [remoteConfig?.supported_languages, LanguageMaps]);

  const changeLanguage = async (value: string) => {
    i18n
      .changeLanguage(value)
      .then(async () => {
        await AsyncStorage.setItem(LS_KEYS.activeLanguage, value);
        dispatch(setCurrentLanguage(value));
      })
      .catch((e) => {
        ErrorHandlerService.handleError(e, {
          // openToast: false,
          toastOptions: {
            type: "error",
            message: "Failed to set language. Please try again",
            position: "top",
          },
        });
      });
  };

  console.log('GLOBAL_VARS.AppVariant', config);

  return (
    <View style={styles.root}>
      {type === "profile" ? (
        <ScreenHeader
          navigation={navigation}
          style={{
            position: "sticky" as any,
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
          }}
          onBackPress={() =>
            navigation.navigate("MainStack", {
              screen: SCREEN_NAMES.ProfileScreen,
            })
          }
        />
      ) : (
        <View
          style={{
            ...StyleHelpers.header,
            borderBottomWidth: 0,
            position: "sticky" as any,
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
          }}
        >
          <div>
            <img src={config.FULL_LOGO_URL} alt={config.APP_NAME} style={{ height: 30, maxWidth: 120 }} />
          </div>
        </View>
      )}

      <View
        style={{
          flexDirection: "column",
          justifyContent: type === "profile" ? "flex-start" : "flex-end",
        }}
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          nestedScrollEnabled={true}
          style={{
            ...StyleHelpers.pageSpacing,
            paddingTop: StyleHelpers.pageSpacing.paddingLeft * 0.5,
          }}
          contentContainerStyle={{
            paddingBottom: StyleHelpers.pageSpacing.paddingLeft * 2.5,
          }}
        >
          {type !== "profile" && (
            <View
              style={{
                marginTop: 100,
              }}
            >
              <CText
                value={t("selectLanguage")}
                style={{ fontWeight: "600" }}
                size={24}
                semiBold
              />
              <CText
                value={t("appWillTranslate")}
                size={12}
                style={{ color: THEME_COLORS.textColor }}
              />
            </View>
          )}

          <View
            style={{
              marginTop: StyleHelpers.pageSpacing.paddingLeft,
            }}
          >
            <FlatList
              data={languageOptions}
              renderItem={({ item }) => (
                <View key={item.id} style={{ marginVertical: 10 }}>
                  <LanguageSelectionCard
                    transTitle={item.transTitle}
                    langText={item.langText}
                    btnText={item.btnText}
                    selected={selectedLanguage === item.id}
                    onPress={() => {
                      setSelectedLanguage(item.id);
                      changeLanguage(item.id);
                    }}
                  />
                </View>
              )}
              keyExtractor={(item) => item.id}
            />
          </View>
        </ScrollView>
      </View>

      {type !== "profile" && (
        <ScreenBottomBar
          style={{
            padding: 16,
            backgroundColor: THEME_COLORS.bgSecondary,
            position: "sticky" as any,
            bottom: 0,
            left: 0,
            right: 0,
            borderBottomWidth: 0,
          }}
        >
          <CButton
            title={t("continue")}
            onPress={() => {
              navigation.navigate(SCREEN_NAMES.EnterPhoneNumberScreen);
            }}
            disable={isEmpty(selectedLanguage)}
          />
        </ScreenBottomBar>
      )}
    </View>
  );
}
